import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { subscribeToPrivateInfo } from 'domains/experts/data/privateInfo';
import { hasAcceptedNda, PrivateInfo } from 'domains/experts/model/privateInfo';

type PrivateInfoLoadState = 'loading' | 'loaded' | 'error';

export interface ExpertNdaBannerViewModel {
  showNdaBanner: boolean;
}

/**
 * Reads the signed-in expert's private/info document so the NDA reminder can
 * be shown from the shared expert layout. The NDA agreement route is excluded
 * because that page is already the action prompted by this banner.
 */
export function useExpertNdaBannerViewModel(): ExpertNdaBannerViewModel {
  const { userAccount } = useAuth();
  const { pathname } = useLocation();
  const accountId = userAccount?.id ?? null;
  const [privateInfo, setPrivateInfo] = useState<PrivateInfo | null>(null);
  const [loadState, setLoadState] = useState<PrivateInfoLoadState>('loading');

  useEffect(() => {
    let active = true;
    setPrivateInfo(null);

    if (!accountId) {
      setLoadState('error');
      return () => {
        active = false;
      };
    }

    setLoadState('loading');
    const unsubscribe = subscribeToPrivateInfo(
      accountId,
      (info) => {
        if (!active) return;
        setPrivateInfo(info);
        setLoadState('loaded');
      },
      () => {
        if (!active) return;
        setPrivateInfo(null);
        setLoadState('error');
      },
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [accountId]);

  const hasSignedNda = privateInfo ? hasAcceptedNda(privateInfo) : false;
  const isNdaAgreementPage = pathname.includes('nda-agreement');

  return {
    showNdaBanner: loadState === 'loaded' && !hasSignedNda && !isNdaAgreementPage,
  };
}
